/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {ɵStyleData} from '@angular/animations';

import {copyStyles, interpolateParams} from '../util';

import {SequenceAst, StyleAst, TransitionAst, TriggerAst} from './animation_ast';
import {AnimationStateStyles, AnimationTransitionFactory} from './animation_transition_factory';
/**
 * \@experimental Animation support is experimental.
 * @param {?} name
 * @param {?} ast
 * @return {?}
 */
export function buildTrigger(name: string, ast: TriggerAst): AnimationTrigger {
  return new AnimationTrigger(name, ast);
}
/**
 * \@experimental Animation support is experimental.
 */
export class AnimationTrigger {
public transitionFactories: AnimationTransitionFactory[] = [];
public fallbackTransition: AnimationTransitionFactory;
public states: {[stateName: string]: AnimationStateStyles} = {};
/**
 * @param {?} name
 * @param {?} ast
 */
constructor(public name: string,
public ast: TriggerAst) {
    ast.states.forEach(ast => {
      const defaultParams = (ast.options && ast.options.params) || {};
      this.states[ast.name] = new AnimationStateStyles(ast.style, defaultParams);
    });

    balanceProperties(this.states, 'true', '1');
    balanceProperties(this.states, 'false', '0');

    ast.transitions.forEach(ast => {
      this.transitionFactories.push(new AnimationTransitionFactory(name, ast, this.states));
    });

    this.fallbackTransition = createFallbackTransition(name, this.states);
  }
/**
 * @return {?}
 */
get containsQueries() { return this.ast.queryCount > 0; }
/**
 * @param {?} currentState
 * @param {?} nextState
 * @return {?}
 */
matchTransition(currentState: any, nextState: any): AnimationTransitionFactory|null {
    const /** @type {?} */ entry = this.transitionFactories.find(f => f.match(currentState, nextState));
    return entry || null;
  }
/**
 * @param {?} currentState
 * @param {?} params
 * @param {?} errors
 * @return {?}
 */
matchStyles(currentState: any, params: {[key: string]: any}, errors: any[]): ɵStyleData {
    return this.fallbackTransition.buildStyles(currentState, params, errors);
  }
}

function AnimationTrigger_tsickle_Closure_declarations() {
/** @type {?} */
AnimationTrigger.prototype.transitionFactories;
/** @type {?} */
AnimationTrigger.prototype.fallbackTransition;
/** @type {?} */
AnimationTrigger.prototype.states;
/** @type {?} */
AnimationTrigger.prototype.name;
/** @type {?} */
AnimationTrigger.prototype.ast;
}

/**
 * @param {?} triggerName
 * @param {?} states
 * @return {?}
 */
function createFallbackTransition(
    triggerName: string,
    states: {[stateName: string]: AnimationStateStyles}): AnimationTransitionFactory {
  const /** @type {?} */ matchers = [(fromState: any, toState: any) => true];
  const /** @type {?} */ animation = new SequenceAst([]);
  const /** @type {?} */ transition = new TransitionAst(matchers, animation);
  return new AnimationTransitionFactory(triggerName, transition, states);
}
/**
 * @param {?} obj
 * @param {?} key1
 * @param {?} key2
 * @return {?}
 */
function balanceProperties(obj: {[key: string]: any}, key1: string, key2: string) {
  if (obj.hasOwnProperty(key1)) {
    if (!obj.hasOwnProperty(key2)) {
      obj[key2] = obj[key1];
    }
  } else if (obj.hasOwnProperty(key2)) {
    obj[key1] = obj[key2];
  }
}
